import { useEffect } from "react";
import { states } from "../../../../js/placeholderData/states";
import { v4 as uuid } from "uuid";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { Trans, useTranslation } from "react-i18next";

import FormInputError from "../../../General/FormInputError";
import DialogBackdrop from "../../../ModalDialogs/DialogBackdrop";
export default function UserInfoDialog({
  agentInfo,
  dialogState,
  setDialogState,
}) {
  const UUID = uuid();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: agentInfo?.email?.address,
      phone: agentInfo?.phone?.phoneNumber,
      addressLine1: agentInfo?.address?.addressLine1,
      addressLine2: agentInfo?.address?.addressLine2,
      city: agentInfo?.address?.city,
      state: agentInfo?.address?.state,
      postalCode: agentInfo?.address?.postalCode,
    },
  });

  const submitUserData = (formData) => {
    alert(`Submitting user date -- ${JSON.stringify(formData)}`);
    setDialogState(false);
  };

  useEffect(() => {
    reset();
  }, [dialogState]);

  return (
    <Dialog
      open={dialogState}
      onClose={() => setDialogState(false)}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("AgentUserInfoDialog-dialogTitle")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label="close update contact information window"
            onClick={() => setDialogState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            {t("AgentUserInfoDialog-dialogDescription")}
          </Dialog.Description>
          <hr />

          <form onSubmit={handleSubmit(submitUserData)}>
            <div className="w-full mx-auto grid grid-cols-1 gap-4 sm:w-4/5 md:w-3/4 lg:w-3/5">
              {/* Email */}
              <div>
                <label
                  id={`${UUID}-email-label`}
                  htmlFor={`${UUID}-email`}
                  className="block mb-1"
                >
                  {t("AgentUserInfoDialog-form-emailLabel")}
                </label>
                <input
                  id={`${UUID}-email`}
                  aria-labelledby={`${UUID}-email-label`}
                  aria-required="true"
                  type="text"
                  placeholder={t("AgentUserInfoDialog-form-emailPlaceholder")}
                  {...register("email", {
                    required: t(
                      "AgentUserInfoDialog-form-emailRequiredMessage"
                    ),
                    pattern: {
                      value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      message: t("AgentUserInfoDialog-form-emailValidMessage"),
                    },
                  })}
                />
              </div>
              {errors.email?.message && (
                <FormInputError errorMessage={errors.email.message} />
              )}

              {/* Mobile */}
              <div>
                <label
                  id={`${UUID}-mobile-label`}
                  htmlFor={`${UUID}-mobile`}
                  className="block mb-1"
                >
                  {t("AgentUserInfoDialog-form-mobileNumberLabel")}
                </label>
                <input
                  id={`${UUID}-mobile`}
                  aria-labelledby={`${UUID}-mobile-label`}
                  aria-describedby={`${UUID}-mobile-help`}
                  type="text"
                  disabled={true}
                />
                {/* <div
                  id={`${UUID}-mobile-help`}
                  className="text-xs text-theme-text-tertiary"
                >
                  * Add a mobile number to receive text notifications.
                </div> */}
                {errors.mobilePhone?.message && (
                  <FormInputError errorMessage={errors.mobilePhone.message} />
                )}
              </div>
              {/* Address */}
              <div>
                <label
                  id={`${UUID}-address-label`}
                  htmlFor={`${UUID}-address`}
                  className="block mb-1"
                >
                  {t("AgentUserInfoDialog-form-addressLabel1")}
                </label>
                <input
                  id={`${UUID}-address`}
                  aria-labelledby={`${UUID}-address-label`}
                  aria-required="true"
                  type="text"
                  disabled={true}
                  {...register("addressLine1")}
                />
              </div>
              <div>
                <label
                  id={`${UUID}-address-label-2`}
                  htmlFor={`${UUID}-address-2`}
                  className="block mb-1"
                >
                  {t("AgentUserInfoDialog-form-addressLabel2")}
                </label>
                <input
                  id={`${UUID}-address-2`}
                  aria-labelledby={`${UUID}-address-label-2`}
                  aria-required="true"
                  type="text"
                  disabled={true}
                  {...register("addressLine2")}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* City */}
                <div className="md:col-span-2">
                  <label
                    id={`${UUID}-city-label`}
                    htmlFor={`${UUID}-city`}
                    className="block mb-1"
                  >
                    {t("AgentUserInfoDialog-form-cityLabel")}
                  </label>
                  <input
                    id={`${UUID}-city`}
                    aria-labelledby={`${UUID}-city-label`}
                    aria-required="true"
                    type="text"
                    disabled={true}
                    {...register("city")}
                  />
                </div>
                {/* State */}
                <div>
                  <label
                    id={`${UUID}-state-label`}
                    htmlFor={`${UUID}-state`}
                    className="block mb-1"
                  >
                    {t("AgentUserInfoDialog-form-stateLabel")}
                  </label>
                  <select
                    id={`${UUID}-state`}
                    aria-labelledby={`${UUID}-state-label`}
                    {...register("state")}
                    disabled={true}
                  >
                    <option value="">Select</option>
                    {states.map((state, idx) => {
                      return (
                        <option
                          key={`state selection - ${idx}`}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {/* ZIP */}
                <div>
                  <label
                    id={`${UUID}-zip-label`}
                    htmlFor={`${UUID}-zip`}
                    className="block mb-1"
                  >
                    {t("AgentUserInfoDialog-form-zipCodeLabel")}
                  </label>
                  <input
                    id={`${UUID}-zip`}
                    aria-labelledby={`${UUID}-zip-label`}
                    aria-required="true"
                    type="text"
                    disabled={true}
                    {...register("postalCode")}
                  />
                </div>
              </div>
            </div>

            {/* <!-- Buttons --> */}
            <div className="block text-right mt-6">
              <button
                type="button"
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setDialogState(false)}
              >
                <Trans
                  i18nKey="AgentUserInfoDialog-form-cancelButton"
                  components={{ span: <span className="sr-only" /> }}
                />
              </button>
              <button className="ui-btn-primary ui-btn-min-w mt-4">
                <Trans
                  i18nKey="AgentUserInfoDialog-form-saveButton"
                  components={{ span: <span className="sr-only" /> }}
                />
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
